import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { useDispatch, useSelector } from "react-redux";
import { resetTokenApi } from "../../../apis/auth.api";
import { getLatestOtpApi } from "../../../apis/user.api";
import { SNACK_BAR_VARIETNS } from "../../../utils/constants";
import { accessToken, refreshToken } from "../../../utils/helper";
import { callSnackBar } from "../../../store/actions/snackbarAction";
import CustomDialog from "./CustomDialog";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
const QRCodeUI = () => {
  const { user } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);
  const [currentAccessToken, setCurrentAccessToken] = useState(
    accessToken.get() || "",
  );
  const [latestOtp, setLatestOtp] = useState(user?.data?.otp?.otp || "");

  const displayOtp = useMemo(() => {
    return latestOtp ? latestOtp.toString() : "";
  }, [latestOtp]);

  const qrValue = JSON.stringify({
    _id: user?.data?._id || "",
    accessToken: currentAccessToken,
    software: 2,
  });

  const onRegenerateClick = async () => {
    try {
      setRefreshing(true);

      const tokenResponse = await resetTokenApi();
      if (tokenResponse?.status !== 1) {
        dispatch(
          callSnackBar(tokenResponse?.message || "Unable to regenerate code", {
            variant: SNACK_BAR_VARIETNS.error,
          }),
        );
        return;
      }

      const nextAccessToken = tokenResponse?.data?.accessToken || "";
      const nextRefreshToken = tokenResponse?.data?.refreshToken || "";

      accessToken.set(nextAccessToken);
      refreshToken.set(nextRefreshToken);
      setCurrentAccessToken(nextAccessToken);

      const otpResponse = await getLatestOtpApi();
      if (otpResponse?.status === 1 && otpResponse?.data?.otp) {
        setLatestOtp(otpResponse.data.otp);
        dispatch(
          callSnackBar("Code regenerated", {
            variant: SNACK_BAR_VARIETNS.suceess,
          }),
        );
      } else {
        dispatch(
          callSnackBar(
            otpResponse?.message || "Token refreshed, but OTP fetch failed",
            { variant: SNACK_BAR_VARIETNS.warning },
          ),
        );
      }
    } catch (e) {
      dispatch(
        callSnackBar("Unable to regenerate code", {
          variant: SNACK_BAR_VARIETNS.error,
        }),
      );
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <CustomDialog id="qr-code-modal" title="App Login">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: { xs: 2, sm: 4 },
          py: 3,
          gap: 3,
          width: "100%",
        }}
      >
        {/* QR Container */}
        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            backgroundColor: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <QRCode
            value={qrValue}
            size={180}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </Paper>

        {/* Instruction */}
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "text.secondary",
            maxWidth: 320,
          }}
        >
          Scan this QR code using the mobile app <br /> to log in securely
        </Typography>

        {/* OTP Section */}
        {displayOtp && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{
                mt: 1,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: "grey.100",
                border: "1px dashed",
                borderColor: "grey.400",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  letterSpacing: { xs: "8px", sm: "12px" },
                  fontWeight: 700,
                  fontFamily: "monospace",
                  textAlign: "center",
                }}
              >
                {displayOtp}
              </Typography>
            </Box>
            <Tooltip title="Regenerate Qr Code / OTP" placement="top">
              <Box
                onClick={!refreshing ? onRegenerateClick : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // gap: 1,

                  mt: 1,
                  px: 2,
                  py: 1.5,

                  borderRadius: 2,
                  cursor: refreshing ? "not-allowed" : "pointer",
                  backgroundColor: "primary.main",
                  color: "#fff",
                }}
              >
                {refreshing ? (
                  <AutorenewIcon
                    sx={{
                      fontSize: "20px",
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(360deg)",
                        },
                      },
                    }}
                  />
                ) : (
                  <RefreshIcon sx={{ fontSize: "20px" }} />
                )}
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Helper Text */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 280,
          }}
        >
          This code is valid for a limited time. Do not share it with anyone.
        </Typography>
      </Box>
    </CustomDialog>
  );
};

export default QRCodeUI;
